/* eslint-disable react-doctor/only-export-components */
import { IconArrowRight } from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	DocsCenterLayout,
	DocsCenterSidebar,
} from "@/components/docs/docs-center-layout";
import { config } from "@/data/config";
import { getHelpCenterNavSections } from "@/lib/docs-content";
import {
	DOCS_API_REFERENCE_PATH,
	DOCS_KNOWLEDGE_BASE_PATH,
	docsHelpCenterLink,
} from "@/lib/docs-paths";
import { redirectIfModuleDisabled } from "@/lib/module-guards";
import {
	isKnowledgeBaseSurfaceEnabled,
	isPublicApiSurfaceEnabled,
} from "@/lib/modules";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/_docs/docs/")({
	beforeLoad: () => redirectIfModuleDisabled("docs", "/"),
	head: () =>
		seo({
			title: "Documentation",
			description: `Help center and guides for ${config.siteName}.`,
			keywords: "documentation, help center, guides",
		}),
	component: DocsHomePage,
});

function DocsHomePage() {
	const sections = getHelpCenterNavSections();
	const articles = sections.flatMap((section) => section.items);

	return (
		<DocsCenterLayout
			mobileNavTitle="Documentation"
			sidebar={
				<DocsCenterSidebar
					title="Documentation"
					description="Help center guides"
					sections={sections}
					activeSlug={null}
					linkForSlug={docsHelpCenterLink}
				/>
			}
		>
			<div className="mx-auto w-full max-w-3xl space-y-8">
				<header className="space-y-2">
					<h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
						Documentation
					</h1>
					<p className="text-muted-foreground">
						Guides for admins and integrators. For endpoint-level detail, open
						the API reference or knowledge base.
					</p>
				</header>

				<div className="grid gap-3">
					{articles.map((article) => (
						<Link
							key={article.slug}
							{...docsHelpCenterLink(article.slug)}
							className="group flex items-start justify-between gap-4 rounded-lg border border-border px-4 py-3 transition-colors hover:bg-muted/40"
						>
							<div className="min-w-0 space-y-1">
								<p className="font-medium text-foreground group-hover:text-primary">
									{article.title}
								</p>
								{article.summary ? (
									<p className="text-sm text-muted-foreground">
										{article.summary}
									</p>
								) : null}
							</div>
							<IconArrowRight className="mt-0.5 size-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
						</Link>
					))}
				</div>

				<div className="flex flex-wrap gap-3 text-sm">
					{isPublicApiSurfaceEnabled() ? (
						<Link
							to={DOCS_API_REFERENCE_PATH}
							className="text-primary underline-offset-4 hover:underline"
						>
							API reference
						</Link>
					) : null}
					{isKnowledgeBaseSurfaceEnabled() ? (
						<Link
							to={DOCS_KNOWLEDGE_BASE_PATH}
							className="text-primary underline-offset-4 hover:underline"
						>
							Knowledge base
						</Link>
					) : null}
				</div>
			</div>
		</DocsCenterLayout>
	);
}
